import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { loginWithCredentials, register } from '../../redux/actions/authActions';
import { toast } from 'react-toastify';
import { APP_DASHBOARD_PATH } from '../../routes';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 480px;
  padding: 0;
  position: relative;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border: 1px solid #e1e5e9;
`;

const TabContainer = styled.div`
  display: flex;
  background: #f8f9fa;
  border-radius: 12px 12px 0 0;
  border-bottom: 1px solid #e1e5e9;
`;

const Tab = styled.button`
  flex: 1;
  padding: 16px 24px;
  background: ${props => (props.$active ? '#10b981' : 'transparent')};
  border: none;
  color: ${props => (props.$active ? 'white' : '#6b7280')};
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  border-radius: ${props => (props.$isFirst ? '12px 0 0 0' : props.$isLast ? '0 12px 0 0' : '0')};
  transition: all 0.2s ease;

  &:hover {
    background: ${props => (props.$active ? '#10b981' : '#e5e7eb')};
    color: ${props => (props.$active ? 'white' : '#374151')};
  }
`;

const FormContainer = styled.div`
  padding: 40px;
`;

const WelcomeText = styled.h2`
  color: #1f2937;
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 8px 0;
  text-align: center;
`;

const SubText = styled.p`
  color: #6b7280;
  font-size: 16px;
  margin: 0 0 32px 0;
  text-align: center;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  color: #374151;
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 14px;
`;

const InputContainer = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 16px 16px 16px 48px;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  color: #1f2937;
  font-size: 16px;
  box-sizing: border-box;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  font-size: 14px;
  font-weight: 600;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  font-size: 12px;
  font-weight: 600;

  &:hover {
    color: #374151;
  }
`;

const SignInButton = styled.button`
  width: 100%;
  padding: 16px;
  background: #10b981;
  border: none;
  border-radius: 8px;
  color: white;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  margin-bottom: 24px;
  transition: background-color 0.2s ease;

  &:hover {
    background: #059669;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DividerContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 24px 0;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: #e5e7eb;
`;

const DividerText = styled.span`
  color: #6b7280;
  padding: 0 16px;
  font-size: 14px;
`;

const SocialButton = styled.button`
  width: 100%;
  padding: 16px;
  background: #24292e;
  border: 1px solid #24292e;
  border-radius: 8px;
  color: white;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
  transition: all 0.2s ease;

  &:hover {
    background: #1b1f23;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const LinkText = styled.p`
  text-align: center;
  color: #6b7280;
  font-size: 14px;
  margin-top: 24px;

  a {
    color: #10b981;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  color: #6b7280;
  font-size: 24px;
  cursor: pointer;
  padding: 4px;
  z-index: 1001;

  &:hover {
    color: #374151;
  }
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 14px;
`;

const AuthModal = ({ isOpen, onRequestClose, history }) => {
  const [activeTab, setActiveTab] = useState('signin');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const isMountedRef = useRef(true);
  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleEmailAuth = async e => {
    e.preventDefault();
    if (!isMountedRef.current) return;

    setIsLoading(true);
    setError('');

    try {
      if (activeTab === 'signin') {
        // Sign in with custom API
        await dispatch(loginWithCredentials(formData.email, formData.password));
      } else {
        // Sign up with custom API - for signup we need username and fullname
        // Using email as username for simplicity, fullname can be extracted from email or set as placeholder
        const username = formData.email.split('@')[0]; // Extract username from email
        const fullname = formData.email.split('@')[0]; // Use same as fullname for now

        await dispatch(register(formData.email, username, fullname, formData.password));
        // After successful registration, automatically sign in
        await dispatch(loginWithCredentials(formData.email, formData.password));
      }

      if (isMountedRef.current) {
        onRequestClose();
        history.push(APP_DASHBOARD_PATH);
        toast.success(`Successfully ${activeTab === 'signin' ? 'signed in' : 'signed up'}!`);
      }
    } catch (error) {
      console.error('Email auth error:', error);
      if (isMountedRef.current) {
        setError(error.response?.data?.detail || error.message || 'Authentication failed');
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  const handleGitHubAuth = async () => {
    try {
      // Redirect to backend GitHub OAuth flow (matches LoginForm.js)
      window.location.href = 'https://api.agent.market/v1/auth/github/login';
    } catch (error) {
      if (isMountedRef.current) {
        setError('GitHub authentication failed. Please try again.');
        setIsLoading(false);
      }
    }
  };

  const resetForm = () => {
    setFormData({ email: '', password: '' });
    setError('');
    setShowPassword(false);
  };

  const handleTabChange = tab => {
    setActiveTab(tab);
    resetForm();
  };

  const handleClose = () => {
    resetForm();
    onRequestClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <CloseButton onClick={handleClose}>×</CloseButton>

        <TabContainer>
          <Tab $active={activeTab === 'signin'} $isFirst onClick={() => handleTabChange('signin')}>
            Sign In
          </Tab>
          <Tab $active={activeTab === 'signup'} $isLast onClick={() => handleTabChange('signup')}>
            Sign Up
          </Tab>
        </TabContainer>

        <FormContainer>
          <WelcomeText>{activeTab === 'signin' ? 'Welcome Back' : 'Create Account'}</WelcomeText>
          <SubText>
            {activeTab === 'signin'
              ? 'Sign in to your account to continue'
              : 'Join Agent Market to get started'}
          </SubText>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <form onSubmit={handleEmailAuth}>
            <FormGroup>
              <Label>Email</Label>
              <InputContainer>
                <InputIcon>@</InputIcon>
                <Input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </InputContainer>
            </FormGroup>

            <FormGroup>
              <Label>Password</Label>
              <InputContainer>
                <InputIcon>••</InputIcon>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                />
                <PasswordToggle type="button" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? 'Hide' : 'Show'}
                </PasswordToggle>
              </InputContainer>
            </FormGroup>

            <SignInButton type="submit" disabled={isLoading}>
              {isLoading ? 'Please wait...' : activeTab === 'signin' ? 'Sign In' : 'Sign Up'}
            </SignInButton>
          </form>

          <DividerContainer>
            <DividerLine />
            <DividerText>or continue with</DividerText>
            <DividerLine />
          </DividerContainer>

          <SocialButton onClick={handleGitHubAuth} disabled={isLoading}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
            </svg>
            Continue with GitHub
          </SocialButton>

          {activeTab === 'signin' ? (
            <LinkText>
              Don't have an account?{' '}
              <a
                href="#signup"
                onClick={e => {
                  e.preventDefault();
                  handleTabChange('signup');
                }}
              >
                Sign up here
              </a>
            </LinkText>
          ) : (
            <LinkText>
              Already have an account?{' '}
              <a
                href="#signin"
                onClick={e => {
                  e.preventDefault();
                  handleTabChange('signin');
                }}
              >
                Sign in here
              </a>
            </LinkText>
          )}
        </FormContainer>
      </ModalContent>
    </ModalOverlay>
  );
};

export default withRouter(AuthModal);
